/**
 * The document view: an epic (or feature) and everything under it, read as a spec. Numbered
 * sections, user stories, acceptance criteria as checklists, defect details, and where each
 * requirement came from. Copy as Markdown for a pull request or a wiki.
 */
import { Check, Copy, FileText, Link2 } from '@orgus/ui/icons';
import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ITEM_KIND_LABEL, criterionText } from '@orgus/protocol';
import type { BacklogItem, Id, ItemLink } from '@orgus/protocol';
import { Button, EmptyState, ErrorState, Markdown, Pressable, Select, cn, toast } from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { useResolveAttachment } from '../attachments/data.ts';
import { ItemState, KindIcon } from '../missions/parts.tsx';
import { docMarkdown, docOutline, docRoots } from './model.ts';
import { AiCaption, KeyLabel, Rollup, SeverityMark } from './parts.tsx';
import { linksOf } from '@orgus/protocol';

export function DocView({
  items,
  links,
  projectId,
  rootId,
  onRoot,
  onOpen,
  end,
  leading,
}: {
  items: readonly BacklogItem[];
  links: readonly ItemLink[];
  projectId: Id | null;
  rootId: Id | null;
  onRoot: (id: Id) => void;
  onOpen: (id: Id) => void;
  /** Controls at the end of the bar: the view switcher. */
  end?: ReactNode;
  /** Controls at the start of the bar: the page's view switch. */
  leading?: ReactNode;
}) {
  const roots = useMemo(() => docRoots(items, projectId), [items, projectId]);
  const root = items.find((i) => i.id === rootId) ?? roots[0];
  const outline = useMemo(() => (root ? docOutline(root.id, [...items]) : []), [root, items]);
  const resolve = useResolveAttachment();
  const byId = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  const [copyError, setCopyError] = useState<UiError | null>(null);

  if (!root) {
    return (
      <div className="flex flex-col gap-3">
        {(leading || end) && (
          <div className="flex h-filter-bar items-center gap-2">
            {leading}
            <span className="ml-auto flex items-center gap-2">{end}</span>
          </div>
        )}
        <div className="rounded-card border border-border bg-surface">
          <EmptyState icon={<FileText />} title="No document to show yet" description="Add stories under an epic, or import a document." />
        </div>
      </div>
    );
  }

  const copy = async () => {
    setCopyError(null);
    try {
      await navigator.clipboard.writeText(docMarkdown(root, [...items]));
      toast.success('Copied as Markdown', `${root.key} ${root.title}`);
    } catch {
      setCopyError({ what: 'Nothing was copied.', why: 'The browser did not allow access to the clipboard.', next: 'Allow clipboard access for this page, then try again.' });
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex min-h-filter-bar flex-wrap items-center gap-2">
        {leading}
        <Select
          aria-label="Document"
          size="sm"
          value={root.id}
          onValueChange={onRoot}
          options={roots.map((r) => ({ value: r.id, label: `${r.key} ${ITEM_KIND_LABEL[r.kind]}: ${r.title}` }))}
          wrapperClassName="w-80"
        />
        <Rollup item={root} items={items} wide />
        <span className="ml-auto flex items-center gap-2">
          <Button variant="ghost" size="sm" icon={<Copy />} onClick={copy}>
            Copy as Markdown
          </Button>
          {end}
        </span>
      </div>
      {copyError && <ErrorState variant="inline" error={copyError} action={<Button variant="ghost" size="sm" onClick={() => setCopyError(null)}>Dismiss</Button>} />}
      <article className="mx-auto w-full max-w-(--layout-width-prose) rounded-card border border-border bg-surface p-8" aria-label={`${root.key} ${root.title}`}>
        <header className="mb-6 flex flex-col gap-2 border-b border-border-subtle pb-6">
          <span className="flex items-center gap-2 text-caption text-fg-subtle">
            <KindIcon kind={root.kind} />
            {ITEM_KIND_LABEL[root.kind]}
            <KeyLabel value={root.key} />
            <ItemState status={root.status} kind={root.kind} className="text-caption" />
          </span>
          <h2 className="text-heading font-semibold text-fg">{root.title}</h2>
          {root.body.trim() && <Markdown source={root.body} resolveAttachment={resolve} className="text-body text-fg-muted" />}
          {root.source && <SourceLine source={root.source} />}
        </header>
        {outline.length === 0 && <p className="text-body text-fg-subtle">Nothing is under it yet.</p>}
        <div className="flex flex-col gap-6">
          {outline.map((s) => {
            const it = s.item;
            const H = s.depth === 0 ? 'h3' : 'h4';
            const rel = linksOf(it.id, links).filter((l) => l.label !== 'Relates to');
            return (
              <section key={it.id} className={cn('flex flex-col gap-2', s.depth > 0 && 'border-l border-border-subtle pl-4')} aria-labelledby={`doc-${it.id}`}>
                <H id={`doc-${it.id}`} className={cn('flex items-baseline gap-2 font-semibold text-fg', s.depth === 0 ? 'text-title' : 'text-lead')}>
                  <span className="shrink-0 text-fg-subtle num">{s.number}</span>
                  <Pressable className="w-auto rounded-control text-left hover:underline focus-visible:underline" onClick={() => onOpen(it.id)}>
                    {it.title}
                  </Pressable>
                </H>
                <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-caption text-fg-subtle">
                  <span className="flex items-center gap-1">
                    <KindIcon kind={it.kind} tooltip={false} />
                    {ITEM_KIND_LABEL[it.kind]}
                  </span>
                  <KeyLabel value={it.key} />
                  <ItemState status={it.status} kind={it.kind} className="text-caption" />
                  {it.estimate != null && <span className="num">{it.estimate} points</span>}
                  {it.defect && <SeverityMark severity={it.defect.severity} />}
                  {it.aiDrafted && <AiCaption />}
                </span>
                {it.body.trim() && <Markdown source={it.body} resolveAttachment={resolve} className="text-body text-fg-muted" />}
                {it.defect && (it.defect.steps || it.defect.expected || it.defect.actual) && (
                  <dl className="flex flex-col gap-1 text-body">
                    {(
                      [
                        ['Environment', it.defect.environment],
                        ['Steps', it.defect.steps],
                        ['Expected', it.defect.expected],
                        ['Actual', it.defect.actual],
                      ] as const
                    )
                      .filter(([, v]) => v)
                      .map(([k, v]) => (
                        <div key={k} className="flex gap-3">
                          <dt className="w-32 shrink-0 text-fg-subtle">{k}</dt>
                          <dd className="min-w-0 whitespace-pre-wrap text-fg-muted">{v}</dd>
                        </div>
                      ))}
                  </dl>
                )}
                {it.acceptance.length > 0 && (
                  <div className="flex flex-col gap-1">
                    <span className="text-caption font-medium text-fg-subtle">Acceptance criteria</span>
                    <ul className="flex flex-col gap-1">
                      {it.acceptance.map((c) => (
                        <li key={c.id} className="flex items-start gap-2 text-body">
                          <span className="flex h-5 shrink-0 items-center">
                            <span className={cn('flex size-4 items-center justify-center rounded-control border', c.done ? 'border-fg-subtle bg-hover text-fg-muted' : 'border-border-strong')} aria-label={c.done ? 'Met' : 'Not met yet'} role="img">
                              {c.done && <Check className="size-3" strokeWidth={3} />}
                            </span>
                          </span>
                          <span className={c.done ? 'text-fg-muted' : 'text-fg'}>{criterionText(c)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {rel.length > 0 && (
                  <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-caption text-fg-subtle">
                    <Link2 className="size-3" aria-hidden />
                    {rel.map((l) => {
                      const o = byId.get(l.otherId);
                      return o ? (
                        <Pressable key={l.link.id} className="w-auto rounded-control hover:text-fg hover:underline" onClick={() => onOpen(o.id)}>
                          {l.label} {o.key}
                        </Pressable>
                      ) : null;
                    })}
                  </p>
                )}
                {it.source?.section && <SourceLine source={it.source} />}
              </section>
            );
          })}
        </div>
      </article>
    </div>
  );
}

function SourceLine({ source }: { source: NonNullable<BacklogItem['source']> }) {
  return (
    <p className="flex items-center gap-2 text-caption text-fg-subtle">
      <FileText className="size-3" aria-hidden />
      From {source.name}
      {source.section ? `, ${source.section}` : ''}
      {source.sections?.length ? <span className="font-mono">({source.sections.join(', ')})</span> : null}
    </p>
  );
}
